import { useState, useEffect, useRef, useCallback } from 'react';
import './Tutorial.css';

function Tutorial({ steps, storageKey, onStep }) {
  const [visible, setVisible]   = useState(false);
  const [step, setStep]         = useState(0);
  const [rect, setRect]         = useState(null);
  const [fading, setFading]     = useState(false);   // true = fading out

  // Keep onStep ref fresh so the effect below doesn't need it as a dep
  const onStepRef = useRef(onStep);
  useEffect(() => { onStepRef.current = onStep; }, [onStep]);

  /* ── Show on first visit ── */
  useEffect(() => {
    const seen = localStorage.getItem(storageKey);
    if (!seen) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [storageKey]);

  /* ── Notify parent whenever step changes ── */
  useEffect(() => {
    if (!visible) return;
    onStepRef.current?.(step, steps[step]);
  }, [step, visible, steps]);

  /* ── Recompute the spotlight rect whenever step / visibility changes ── */
  const refreshRect = useCallback(() => {
    const anchorId = steps[step]?.anchorId;
    if (!anchorId) { setRect(null); return; }
    const el = document.getElementById(anchorId);
    if (el) setRect(el.getBoundingClientRect());
    else     setRect(null);
  }, [step, steps]);

  useEffect(() => {
    if (!visible) return;
    const anchorId = steps[step]?.anchorId;
    if (!anchorId) { setRect(null); return; }

    const el = document.getElementById(anchorId);
    if (!el) { setRect(null); return; }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Wait for scroll to settle, then snapshot the rect
    const t = setTimeout(() => {
      setRect(el.getBoundingClientRect());
    }, 450);
    return () => clearTimeout(t);
  }, [step, visible, steps]);

  /* ── Keep rect live during scroll / resize ── */
  useEffect(() => {
    if (!visible) return;
    window.addEventListener('scroll', refreshRect, { passive: true });
    window.addEventListener('resize', refreshRect);
    return () => {
      window.removeEventListener('scroll', refreshRect);
      window.removeEventListener('resize', refreshRect);
    };
  }, [visible, refreshRect]);

  /* ── Close with fade-out ── */
  const close = useCallback(() => {
    onStepRef.current?.(-1, null);   // signal parent: tutorial closing
    localStorage.setItem(storageKey, 'true');
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      setFading(false);
      setStep(0);
      setRect(null);
    }, 300);
  }, [storageKey]);

  const next = () => {
    if (step < steps.length - 1) setStep(s => s + 1);
    else close();
  };

  if (!visible) return null;

  const currentStep = steps[step];
  const hasSpot     = !!(currentStep.anchorId && rect);

  // Auto-place card on the opposite side from the highlighted element so it never covers it.
  // If element center is in the bottom half → card goes to top; otherwise card goes to bottom.
  // For anchor-less steps, fall back to cardAlignment (default 'bottom').
  const isBottom = hasSpot
    ? (rect.top + rect.height / 2) <= window.innerHeight * 0.5
    : (currentStep.cardAlignment !== 'top');

  return (
    <div className={`tutorial-root${fading ? ' tutorial-fading' : ''}`}>

      {/* ── Dark overlay ── */}
      {hasSpot ? (
        <>
          {/* Click-blocker so the dark area can't accidentally trigger underlying UI */}
          <div className="tutorial-overlay tutorial-overlay-transparent" />
          {/* Spotlight cutout via box-shadow */}
          <div
            className="tutorial-spotlight"
            style={{
              left:   rect.left   - 12,
              top:    rect.top    - 12,
              width:  rect.width  + 24,
              height: rect.height + 24,
            }}
          />
        </>
      ) : (
        /* Solid overlay — no cutout */
        <div className="tutorial-overlay" />
      )}

      {/* ── Tutorial card ── */}
      <div className={`tutorial-card tutorial-card-${isBottom ? 'bottom' : 'top'}`}>

        {/* Progress dots */}
        <div className="tutorial-dots">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`tutorial-dot${i === step ? ' tutorial-dot-active' : ''}`}
            />
          ))}
        </div>

        {/* Text */}
        <div className="tutorial-title">{currentStep.title}</div>
        <div className="tutorial-desc">{currentStep.description}</div>

        {/* Buttons */}
        <div className="tutorial-actions">
          <button className="tutorial-skip" onClick={close}>Skip All</button>
          <button className="tutorial-next" onClick={next}>
            {step === steps.length - 1 ? 'Done' : 'Next →'}
          </button>
        </div>
      </div>

    </div>
  );
}

export default Tutorial;
